import { baseApi } from '@/shared/api'
import {
  CreateWorkOrderRequest,
  WorkOrderResponse,
  WorkOrder,
  WorkOrderDto,
  WorkOrderHistoryResponse,
  GetWorkOrdersResponse,
  GetWorkOrdersParams,
  type FileResponse,
} from './types'
import { mapWorkOrder } from '../lib/mapWorkOrder'
import { FILES_TAG } from '@/shared/api/tags'

export const workorderApi = baseApi.injectEndpoints({
  endpoints: build => ({
    createWorkOrder: build.mutation<WorkOrderResponse, CreateWorkOrderRequest>({
      query: body => ({
        url: `/work-orders`,
        method: 'POST',
        body,
      }),
    }),
    getWorkOrders: build.query<GetWorkOrdersResponse, GetWorkOrdersParams>({
      query: params => ({
        url: `/work-orders`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: {
        data: WorkOrderDto[]
        links: any
        meta: any
      }) => ({
        data: response.data,
        meta: response.meta,
      }),
    }),
    getWorkOrderDetail: build.query<WorkOrder, { id: string }>({
      query: ({ id }) => ({
        url: `/work-orders/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: WorkOrderDto }) =>
        mapWorkOrder(response.data),
    }),
    updateWorkOrder: build.mutation<
      WorkOrder,
      { id: number; updateData: Partial<WorkOrderDto> }
    >({
      query: ({ id, updateData }) => ({
        url: `/work-orders/${id}`,
        method: 'PUT',
        body: updateData,
      }),
      transformResponse: (response: { data: WorkOrderDto }) =>
        mapWorkOrder(response.data),
    }),
    updateWorkOrderStatus: build.mutation<
      WorkOrder,
      { id: string; status: string; rejected_reason?: string }
    >({
      query: ({ id, status, rejected_reason }) => ({
        url: `/work-orders/${id}/update-status`,
        method: 'PATCH',
        body: { status, rejected_reason },
      }),
      transformResponse: (response: { data: WorkOrderDto }) =>
        mapWorkOrder(response.data),
    }),
    updateWorkOrderDraft: build.mutation<
      WorkOrder,
      { id: string; is_draft: boolean }
    >({
      query: ({ id, is_draft }) => ({
        url: `/work-orders/${id}/draft`,
        method: 'PATCH',
        body: { is_draft },
      }),
      transformResponse: (response: { data: WorkOrderDto }) =>
        mapWorkOrder(response.data),
    }),
    getWorkOrderHistory: build.query<
      WorkOrderHistoryResponse,
      { workOrderId: number }
    >({
      query: ({ workOrderId }) => ({
        url: `/work-orders/${workOrderId}/history`,
        method: 'GET',
      }),
    }),
    addFile: build.mutation<
      FileResponse,
      { deliveryNoteId: number; file: File }
    >({
      query: ({ deliveryNoteId, file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `/delivery-notes/${deliveryNoteId}/files`,
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: (result, error, { deliveryNoteId }) => [
        { type: FILES_TAG, id: deliveryNoteId },
      ],
    }),

    showFile: build.query<Blob, { deliveryNoteId: number; fileId: number }>({
      query: ({ deliveryNoteId, fileId }) => ({
        url: `/delivery-notes/${deliveryNoteId}/files/${fileId}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    getFiles: build.query<FileResponse[], { deliveryNoteId: number }>({
      query: ({ deliveryNoteId }) => ({
        url: `/delivery-notes/${deliveryNoteId}/files`,
        method: 'GET',
      }),

      providesTags: (result, error, { deliveryNoteId }) => [
        { type: FILES_TAG, id: deliveryNoteId },
      ],
    }),

    deleteFile: build.mutation<
      void,
      { deliveryNoteId: number; fileId: number }
    >({
      query: ({ deliveryNoteId, fileId }) => ({
        url: `/delivery-notes/${deliveryNoteId}/files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { deliveryNoteId }) => [
        { type: FILES_TAG, id: deliveryNoteId },
      ],
    }),
    exportWorkOrderReport: build.mutation<Blob, { workOrderId: number }>({
      query: ({ workOrderId }) => ({
        url: `work-orders/${workOrderId}/export-csv-report`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
  }),
})

export const {
  useCreateWorkOrderMutation,
  useGetWorkOrdersQuery,
  useGetWorkOrderDetailQuery,
  useUpdateWorkOrderMutation,
  useUpdateWorkOrderStatusMutation,
  useUpdateWorkOrderDraftMutation,
  useAddFileMutation,
  useShowFileQuery,
  useGetFilesQuery,
  useDeleteFileMutation,
  useGetWorkOrderHistoryQuery,
  useExportWorkOrderReportMutation,
} = workorderApi
